import React from 'react';
import { ArrowRight, Bot, Globe, Workflow, Sparkles, Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageMeta } from '../hooks/usePageMeta';
import { Reveal } from '../components/ui-kit/Reveal';
import { BrandButton } from '../components/ui-kit/BrandButton';
import CTA from '../components/CTA';
import ContactSection from '../components/ContactSection';

// Brand-agent is the lead product → first, largest, full-bleed.
const HERO_SECTION = {
  id: 'brandiagentti',
  icon: Bot,
  eyebrow: 'Pääpalvelu',
  title: 'Brändiagentti',
  lead:
    'Luomme yrityksellesi brändipersoonan somesisältöihin ja samaan persoonaan perustuvan AI-agentin nykyiselle verkkosivustollesi.',
  paragraphs: [
    'Brändiagentti yhdistää tunnistettavan AI-hahmon ja keskustelevan avustajan yhdeksi kokonaisuudeksi. Hahmolla on oma luonne, äänensävy ja visuaalinen rooli — se ei ole geneerinen chatbot, vaan osa yrityksesi brändiä.',
    'Agentti voi auttaa vierailijaa löytämään oikean palvelun, vastata yleisimpiin kysymyksiin, kerätä yhteystiedot ja ohjata kohti tarjouspyyntöä tai ajanvarausta. Aukioloajat sovitaan toteutuksessa.',
  ],
  bullets: [
    'Brändin mukainen ulkoasu, ääni ja luonne',
    'Sama brändipersoona somesisällöissä ja verkkosivun agentissa',
    'Auttaa kävijää löytämään oikean palvelun ja jättämään yhteydenoton',
    'Vastaa yleisimpiin kysymyksiin sivustolla',
    'Kytkentä valittuihin taustajärjestelmiin (esim. lomakkeet, CRM, kalenteri) toteutuksen mukaan',
  ],
};

// Supporting services (Agencive does NOT sell websites)
const SUPPORTING = [
  {
    id: 'automaatiot',
    icon: Workflow,
    title: 'Automaatiot ja integraatiot',
    lead:
      'Kytkennät valittuihin taustajärjestelmiin niin, että liidit ja tiedot päätyvät oikeaan paikkaan toteutuksen mukaisesti.',
    paragraphs: [
      'Suunnittelemme brändiagentin ja käytössäsi olevien järjestelmien välille kytkennät, jotka on rajattu tarpeen mukaan. Emme lupaa yleisiä valmispaketteja — vahvistamme aina, mitkä integraatiot kuuluvat toimitukseen.',
    ],
    bullets: [
      'Yhteydenotot valittuun kanavaan (sähköposti tai CRM)',
      'Tarpeen mukaiset kytkennät kalenteriin tai ajanvaraukseen',
      'Mittarit ja konversiotapahtumat toteutuksen laajuudessa',
    ],
  },
  {
    id: 'sisalto',
    icon: Sparkles,
    title: 'Sisältö ja brändihahmo',
    lead:
      'Suunnittelemme brändihahmon visuaalisen ilmeen, äänensävyn ja roolin sosiaalisessa mediassa sekä verkkosivulla.',
    paragraphs: [
      'Brändihahmo pysyy yhtenäisenä somesisällöissä ja verkkosivun agentissa. Sisältötuotannon laajuus sovitaan aina erikseen — kertaluontoisena tai jatkuvana yhteistyönä.',
    ],
    bullets: [
      'Yhtenäinen visuaalinen tyyli ja äänensävy',
      'Some-sisällöt brändin mukaisina',
      'Toimitus laajuudessa, joka sovitaan yhdessä',
    ],
  },
  {
    id: 'analytiikka',
    icon: Globe,
    title: 'Analytiikka ja optimointi',
    lead:
      'Mittaus, integraatiot ja jatkuva optimointi sovitaan projektikohtaisesti toimituksen laajuuden mukaan.',
    paragraphs: [
      'Suunnittelemme yhdessä, mitä mittareita ja konversiotapahtumia otetaan käyttöön ja miten agenttia kehitetään käytön perusteella. Emme lupaa yleistä valmispakettia — laajuus sovitaan aina erikseen.',
    ],
    bullets: [
      'Mittarit toteutuksen laajuudessa',
      'Konversiotapahtumat, kun ne kuuluvat toimitukseen',
      'Iteratiivinen kehitys sovitusti',
    ],
  },
];

const Palvelut = () => {
  usePageMeta({
    title: 'Agencive – Palvelut brändiagentin ympärille',
    description:
      'Tutustu Agenciven brändiagentteihin, automaatioihin, sisältöön ja integraatioihin.',
    path: '/palvelut',
  });

  return (
    <main className="bg-bg">
      {/* Page hero */}
      <section className="pt-28 md:pt-32 pb-12 md:pb-16 bg-bg">
        <div className="container-page max-w-3xl">
          <Reveal>
            <span className="eyebrow text-ink-muted">Palvelut</span>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-5 text-h1 text-ink">Brändiagentti ja sitä tukevat palvelut</h1>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-5 text-body-lg text-ink-muted">
              Rakennamme yrityksille tunnistettavia brändiagentteja ja asennamme ne
              olemassa olevalle verkkosivustollesi. Automaatiot, sisältö ja mittaus
              voidaan sopia agentin ympärille projektikohtaisesti.
            </p>
          </Reveal>

          <Reveal delay={180}>
            <nav aria-label="Palvelut" className="mt-8 flex flex-wrap gap-2">
              <a
                href={`#${HERO_SECTION.id}`}
                className="inline-flex items-center h-9 px-4 rounded-pill bg-ink text-white text-[13.5px] hover:bg-dark transition-colors"
              >
                {HERO_SECTION.title}
              </a>
              {SUPPORTING.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="inline-flex items-center h-9 px-4 rounded-pill border border-line text-[13.5px] text-ink-muted hover:text-ink hover:border-ink/40 transition-colors"
                >
                  {s.title}
                </a>
              ))}
            </nav>
          </Reveal>
        </div>
      </section>

      {/* Lead product — Brändiagentti */}
      <section
        id={HERO_SECTION.id}
        data-testid={`service-detail-${HERO_SECTION.id}`}
        className="scroll-mt-28 pb-16 md:pb-24"
      >
        <div className="container-page">
          <Reveal>
            <div className="rounded-hero bg-ink text-white p-8 md:p-14 relative overflow-hidden">
              <div
                aria-hidden
                className="absolute inset-0 opacity-[0.05]"
                style={{
                  backgroundImage:
                    'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
                  backgroundSize: '72px 72px',
                }}
              />
              <div className="relative grid lg:grid-cols-12 gap-8 lg:gap-12">
                <div className="lg:col-span-7">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-input bg-white/10 text-brand">
                      <HERO_SECTION.icon className="w-[19px] h-[19px]" strokeWidth={1.75} />
                    </span>
                    <span className="eyebrow text-white/70">{HERO_SECTION.eyebrow}</span>
                  </div>
                  <h2 className="mt-5 text-h1 text-white">{HERO_SECTION.title}</h2>
                  <p className="mt-5 text-body-lg text-white/85 max-w-2xl">{HERO_SECTION.lead}</p>
                  {HERO_SECTION.paragraphs.map((p, i) => (
                    <p key={i} className="mt-4 text-body text-white/80 max-w-2xl">{p}</p>
                  ))}
                  <div className="mt-8">
                    <BrandButton
                      to="/yhteys#contact-form"
                      variant="primary"
                      size="lg"
                      iconRight={ArrowRight}
                      className="shadow-[0_8px_24px_rgba(47,111,94,0.25)]"
                    >
                      Varaa maksuton kartoitus
                    </BrandButton>
                  </div>
                </div>

                <div className="lg:col-span-5">
                  <div className="rounded-card bg-white/5 border border-white/15 p-6 md:p-7 backdrop-blur-[6px]">
                    <div className="text-eyebrow uppercase text-white/65">Mitä saat</div>
                    <ul className="mt-4 space-y-3">
                      {HERO_SECTION.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-3 text-[14.5px] text-white leading-snug">
                          <span className="mt-1 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand/30 text-brand">
                            <Check className="w-3 h-3" strokeWidth={3} />
                          </span>
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Supporting services */}
      <section className="pb-20 md:pb-28 bg-bg">
        <div className="container-page">
          <Reveal>
            <h2 className="text-h2 text-ink max-w-2xl">Brändiagentin ympärille rakentuvat lisäpalvelut</h2>
          </Reveal>
          <Reveal delay={80}>
            <p className="mt-4 text-body-lg text-ink-muted max-w-2xl">
              Voit aloittaa pelkällä brändiagentilla, tai rakentaa sen ympärille
              automaatiot ja sisältötuotannon. Agencive ei toimita erillisiä verkkosivuprojekteja
              — asennamme brändiagentin olemassa olevalle sivustollesi.
            </p>
          </Reveal>

          <div className="mt-14 space-y-16 md:space-y-20">
            {SUPPORTING.map((s, idx) => {
              const Icon = s.icon;
              const flip = idx % 2 === 1;
              return (
                <article
                  key={s.id}
                  id={s.id}
                  data-testid={`service-detail-${s.id}`}
                  className="scroll-mt-28 grid md:grid-cols-12 gap-8 md:gap-12 items-start"
                >
                  <div className={`md:col-span-7 ${flip ? 'md:order-2' : ''}`}>
                    <Reveal>
                      <div className="flex items-center gap-3">
                        <span className="inline-flex h-11 w-11 items-center justify-center rounded-input bg-brand-soft text-brand">
                          <Icon className="w-[19px] h-[19px]" strokeWidth={1.75} />
                        </span>
                        <span className="eyebrow text-ink-muted">{String(idx + 2).padStart(2, '0')}</span>
                      </div>
                    </Reveal>
                    <Reveal delay={80}>
                      <h3 className="mt-5 text-h2 text-ink">{s.title}</h3>
                    </Reveal>
                    <Reveal delay={140}>
                      <p className="mt-5 text-body-lg text-ink-muted max-w-2xl">{s.lead}</p>
                    </Reveal>
                    {s.paragraphs.map((p, i) => (
                      <Reveal key={i} delay={180 + i * 40}>
                        <p className="mt-4 text-body text-ink-muted max-w-2xl">{p}</p>
                      </Reveal>
                    ))}
                  </div>

                  <div className={`md:col-span-5 ${flip ? 'md:order-1' : ''}`}>
                    <Reveal delay={120}>
                      <div className="rounded-card bg-surface border border-line p-6 md:p-7">
                        <div className="text-eyebrow uppercase text-ink-muted">Hyödyt</div>
                        <ul className="mt-4 space-y-3">
                          {s.bullets.map((b) => (
                            <li key={b} className="flex items-start gap-3 text-[14.5px] text-ink leading-snug">
                              <span className="mt-1 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand-soft text-brand">
                                <Check className="w-3 h-3" strokeWidth={3} />
                              </span>
                              <span>{b}</span>
                            </li>
                          ))}
                        </ul>
                        <div className="mt-6 pt-5 border-t border-line">
                          <BrandButton to="/yhteys#contact-form" variant="ghost" iconRight={ArrowRight} className="px-0">
                            Varaa maksuton kartoitus
                          </BrandButton>
                        </div>
                      </div>
                    </Reveal>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <CTA />
      <ContactSection />
    </main>
  );
};

export default Palvelut;
